/*!
 * gara-oil-visc-filter.js - constrain engine-oil cards to the rider's bike weight.
 *
 * The problem it solves: engine oils are UNIVERSAL-fit, so the YMM fitment filter
 * can't narrow them. A rider who clicks "Shop oil & filters for this bike" from the
 * Quick Maintenance Sheet (which already shows e.g. 10W-40) would otherwise see every
 * weight on the shelf (20W-50, 15W-50, 10W-40, 10W-30, ...). This hides the engine
 * oils whose multigrade weight does not match the bike's manufacturer-spec viscosity.
 *
 * SCOPE - this ONLY touches cards the server flagged data-engine-oil (4-stroke engine
 * oils). Fork oil, gear oil, filters, washers, everything else is left exactly as the
 * YMM fitment filter leaves it. A flagged card with no parseable weight in its title
 * (monograde, 2-stroke) is NEVER hidden - we only hide when we can prove the weight
 * differs.
 *
 * WHERE the bike weight comes from (first hit wins):
 *   1. URL ?oilv=<weight>            - set by the QMS oil link. May be a set:
 *                                      "10W-40,10W-50" when the bike accepts more
 *                                      than one (honest to alternate manufacturer specs).
 *   2. sessionStorage gara_oil_visc  - {slug, weights} persisted so the filter survives
 *                                      server pagination (?page=2 drops the URL param).
 *                                      Tied to the bike slug so it self-clears the moment
 *                                      the rider picks a DIFFERENT bike on the page.
 *   3. window.GARA_OIL_VISC_MAP      - the SPINE's gen-aware bike->weight projection
 *                                      (gara-oil-visc-map.js). Used when the rider picks a
 *                                      bike in the YMM dropdown WITHOUT coming through the
 *                                      QMS oil link (the common case the page filter missed:
 *                                      a 2014 MT-09 was showing every weight). Resolved by
 *                                      the selected year so a mid-life weight change splits.
 *                                      A value of "2T" is a STROKE MARKER, not a weight: that
 *                                      bike runs pre-mix, so it takes the 2-stroke view (the
 *                                      same one ?oilstroke=2t gives), and a 4th element on that
 *                                      span carries the bike's GEARBOX weight. Electric bikes are
 *                                      absent from the map by design (no engine oil exists on them).
 *                                      If a bike IS selected but has no spine weight, engine
 *                                      oils are HIDDEN (honest: we can't confirm a fit) — we
 *                                      never fall back to showing every weight. (Benjamin 2026-06-08.)
 *
 * THE 2-STROKE VIEW (Benjamin approved, 2026-08-17): a pre-mix bike sees 2-stroke oils AND
 * the bottles of its own GEARBOX weight. A two-stroke has no engine oil but it does have a
 * transmission that takes an ordinary multigrade, so hiding every 4-stroke bottle sent that
 * rider away from the one oil they can actually buy. The gearbox weight comes from the same
 * spine pass (?oilgb, the session, or the map span's 4th element) and is NEVER guessed: with
 * no gearbox weight on file the view is exactly what it was before, 2-stroke oils only.
 *
 * Source of truth for the weight is the spine (canonical/vehicle_specs): the QMS passes it
 * via ?oilv / ?oilstroke / ?oilgb, and the same spine pass emits GARA_OIL_VISC_MAP for dropdown
 * selections. This file never guesses a viscosity.
 */
(function (global) {
  'use strict';

  var SS_KEY = 'gara_oil_visc';
  var STYLE_ID = 'gara-oil-visc-style';
  var HIDE_CLASS = 'gara-oil-visc-hidden';

  var doc = global.document;
  if (!doc) return;

  // "10W-40" / "10w40" / " 10 W - 40 " -> "10W40". Anything without a weight -> ''.
  function normWeight(s) {
    if (s == null) return '';
    var m = String(s).toUpperCase().match(/(\d{1,2})\s*W\s*-?\s*(\d{2})/);
    return m ? (m[1] + 'W' + m[2]) : '';
  }

  // A 2-stroke / pre-mix / injector oil. When the bike takes a 4-stroke multigrade
  // (it has an accepted weight), these are categorically WRONG and must be hidden
  // even though they have no W-weight to compare (e.g. "Motul 510 2T Premix").
  function isTwoStroke(s) {
    return /\b2\s*[ST]\b|2-?\s*STROKE|PRE-?MIX|PREMIX|INJECT/i.test(String(s == null ? '' : s));
  }

  // The bike's accepted weights as a normalized set. Splits on , / | & + so a bike
  // that accepts "10W-40 / 10W-50" filters to either. Only ever receives real weights:
  // the spine map's "2T" stroke marker is routed in resolveView BEFORE this is called,
  // because normWeight needs a digit-W-digits pattern and would drop "2T" to an empty
  // set (which reads as "no bike selected" = show the whole 4-stroke shelf).
  function parseAccepted(raw) {
    var out = {};
    if (!raw) return out;
    String(raw).split(/[,\/|&+]+/).forEach(function (tok) {
      var w = normWeight(tok);
      if (w) out[w] = 1;
    });
    return out;
  }

  function slugify(s) {
    return String(s == null ? '' : s).toLowerCase().trim()
      .replace(/\s+/g, '-').replace(/\//g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  }

  function currentBikeSlug() {
    try {
      var raw = global.localStorage.getItem('gara_ymm');
      if (!raw) return '';
      var v = JSON.parse(raw);
      if (!v || !v.make || !v.model) return '';
      return slugify(v.make) + '-' + slugify(v.model);
    } catch (_) { return ''; }
  }

  function currentBikeYear() {
    try {
      var v = JSON.parse(global.localStorage.getItem('gara_ymm') || 'null');
      return v && v.year ? Number(v.year) : 0;
    } catch (_) { return 0; }
  }

  // Resolve the bike's SPINE map span for the selected year.
  // spans = [[year_start, year_end, "10W40"], ...] or, on a 2-stroke,
  // [[year_start, year_end, "2T", "10W40"?], ...] whose 4th element is the bike's GEARBOX
  // weight. Among spans containing the year, take the latest-starting (mirrors resolveSizes'
  // "most specific gen wins"). Weights are already in normWeight() form. null when the year
  // falls outside every span.
  function resolveSpineSpan(spans, year) {
    if (!spans || !spans.length) return null;
    var best = null, bestYs = -1;
    for (var i = 0; i < spans.length; i++) {
      var s = spans[i], ys = Number(s[0]) || 0, ye = Number(s[1]) || ys;
      if (year && (year < ys || year > ye)) continue;
      if (ys >= bestYs) { best = s; bestYs = ys; }
    }
    if (!best && !year) best = spans[0];      // no year picked -> first span
    return best || null;
  }

  // The bike's GEARBOX weight from the spine map, for a bike already known to be 2-stroke.
  // Consulted when 2-stroke mode arrived by URL param or session (the QMS oil link, or server
  // pagination) instead of from the map itself, so every entry path shows the same cards.
  // '' when the spine holds no gearbox weight for the selected year.
  function spineGearboxWeight(slug, year) {
    var MAP = global.GARA_OIL_VISC_MAP;
    if (!MAP || !slug) return '';
    var span = resolveSpineSpan(MAP[slug], year);
    return (span && span[2] === '2T' && span[3]) ? normWeight(span[3]) : '';
  }

  function readSession() {
    try { return JSON.parse(global.sessionStorage.getItem(SS_KEY) || 'null'); }
    catch (_) { return null; }
  }
  function writeSession(obj) {
    try {
      if (obj) global.sessionStorage.setItem(SS_KEY, JSON.stringify(obj));
      else global.sessionStorage.removeItem(SS_KEY);
    } catch (_) {}
  }

  function getParam(name) {
    try { return new global.URLSearchParams(global.location.search).get(name); }
    catch (_) {
      var m = global.location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
      return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
    }
  }

  // Resolve the current view: a 4-stroke weight set (from ?oilv) OR 2-stroke mode
  // (from ?oilstroke=2t, optionally with the bike's gearbox weight in ?oilgb). URL param
  // wins and is persisted tied to the bike (so server pagination keeps the filter); else
  // reuse the persisted value while the SAME bike is still selected.
  // Returns {weights:{...}, stroke:'2t'|null, gb:'10W40'|''}.
  function resolveView() {
    var slug = currentBikeSlug();
    var year = currentBikeYear();
    var oilv = getParam('oilv');
    var stroke = (getParam('oilstroke') || '').toLowerCase();
    if (oilv) { writeSession({ slug: slug, raw: oilv }); return { weights: parseAccepted(oilv), stroke: null }; }
    if (stroke === '2t') {
      // ?oilgb is the QMS-supplied gearbox weight; with none on the URL fall back to the
      // spine map for this bike, so the link and the dropdown agree on what a 2-stroke sees.
      var gbParam = normWeight(getParam('oilgb')) || spineGearboxWeight(slug, year);
      writeSession({ slug: slug, stroke: '2t', gb: gbParam });
      return { weights: {}, stroke: '2t', gb: gbParam };
    }
    var sess = readSession();
    if (sess && sess.slug && slug && sess.slug === slug) {
      if (sess.stroke === '2t') {
        return { weights: {}, stroke: '2t', gb: sess.gb || spineGearboxWeight(slug, year) };
      }
      if (sess.raw) return { weights: parseAccepted(sess.raw), stroke: null };
    }
    // No QMS-supplied weight: fall back to the SPINE map for the selected bike, so picking
    // a bike in the YMM dropdown (not via the QMS oil link) also constrains engine oils.
    var MAP = global.GARA_OIL_VISC_MAP;
    if (MAP && slug) {
      var span = resolveSpineSpan(MAP[slug], year);
      var w = span ? span[2] : '';
      // "2T" is a STROKE MARKER the builder writes in the weight slot for a pre-mix bike,
      // never a weight. It must be routed here, before parseAccepted() can flatten it to an
      // empty set. Exact match by contract with build_oil_kit_from_spine (one generator,
      // one literal); the view is identical to ?oilstroke=2t and persists the same way. The
      // span's 4th element, when present, is that bike's GEARBOX weight.
      if (w === '2T') {
        var gbMap = (span && span[3]) ? normWeight(span[3]) : '';
        writeSession({ slug: slug, stroke: '2t', gb: gbMap });
        return { weights: {}, stroke: '2t', gb: gbMap };
      }
      if (w) return { weights: parseAccepted(w), stroke: null };
      // Bike selected but no spine weight for it -> hide engine oils (honest: can't confirm
      // a fit). NOT keys-empty (which means "no bike, show all"); flagged explicitly.
      return { weights: {}, stroke: null, hideAll: true };
    }
    // No bike selected (or the spine map asset didn't load) -> show everything.
    return { weights: {}, stroke: null };
  }

  function ensureStyle() {
    if (doc.getElementById(STYLE_ID)) return;
    var st = doc.createElement('style');
    st.id = STYLE_ID;
    st.textContent = '.' + HIDE_CLASS + '{display:none !important;}';
    (doc.head || doc.documentElement).appendChild(st);
  }

  function titleOf(card) {
    var el = card.querySelector('.gara-pcard-title');
    return el ? el.textContent : (card.getAttribute('aria-label') || '');
  }

  function apply() {
    var view = resolveView();
    var accepted = view.weights, keys = Object.keys(accepted);
    var cards = doc.querySelectorAll('.gara-pcard[data-engine-oil]');
    if (!cards.length) return;
    ensureStyle();
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var title = titleOf(card);
      if (view.stroke === '2t') {
        // 2-stroke bike: its pre-mix oils, PLUS the bottles of its own GEARBOX weight (that
        // bike has no engine oil but it does have a transmission to fill). Every other
        // 4-stroke weight stays hidden, and with no gearbox weight on file this is exactly
        // the old behavior: 2-stroke oils only.
        if (isTwoStroke(title)) card.classList.remove(HIDE_CLASS);
        else if (view.gb && normWeight(title) === view.gb) card.classList.remove(HIDE_CLASS);
        else card.classList.add(HIDE_CLASS);
        continue;
      }
      if (view.hideAll) {
        // A bike IS selected but we have no spine weight for it -> hide every engine oil
        // (honest: we can't confirm any weight fits, so don't show "all weights").
        card.classList.add(HIDE_CLASS);
        continue;
      }
      // No bike selected at all -> nothing to enforce; make sure nothing stays hidden by us.
      if (!keys.length) { card.classList.remove(HIDE_CLASS); continue; }
      // The bike takes a 4-stroke multigrade (it has an accepted weight), so a
      // 2-stroke / pre-mix oil never belongs here, hide it regardless of weight.
      if (isTwoStroke(title)) { card.classList.add(HIDE_CLASS); continue; }
      var w = normWeight(title);
      // Otherwise hide ONLY when we can read a weight AND it isn't one the bike accepts.
      if (w && !accepted[w]) card.classList.add(HIDE_CLASS);
      else card.classList.remove(HIDE_CLASS);
    }
  }

  // The rider changed/cleared their bike on the page -> a param-supplied weight may no
  // longer match. Drop the persisted weight unless it still belongs to the new bike.
  function onYmmChange(e) {
    var detail = e && e.detail;
    var sess = readSession();
    if (sess) {
      var newSlug = detail && detail.make && detail.model
        ? slugify(detail.make) + '-' + slugify(detail.model) : '';
      if (!newSlug || sess.slug !== newSlug) writeSession(null);
    }
    apply();
  }

  function init() {
    apply();
    doc.addEventListener('gara-ymm:change', onYmmChange);
    // Re-apply on Shopify section reloads (filter / sort) and on lazy/async grid fills.
    doc.addEventListener('shopify:section:load', apply);
    var grid = doc.querySelector('#main-collection, .collection, main') || doc.body;
    if (grid && global.MutationObserver) {
      var obs = new global.MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          for (var j = 0; j < muts[i].addedNodes.length; j++) {
            var n = muts[i].addedNodes[j];
            if (n.nodeType === 1 &&
                ((n.matches && n.matches('.gara-pcard[data-engine-oil]')) ||
                 (n.querySelector && n.querySelector('.gara-pcard[data-engine-oil]')))) {
              apply();
              return;
            }
          }
        }
      });
      obs.observe(grid, { childList: true, subtree: true });
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window !== 'undefined' ? window : this);
